import React, { useEffect, useState } from "react";
import { useAllocationHistory } from "../../../../api/queries/leaveQueries";
import { getApiErrorMessage } from "../../../../utils/apiError";
import { formatDateTime, formatPlainDate } from "../../../../utils/dateTime";
import { allocationName } from "./allocationFormat";

// HR changes (CREATE/UPDATE) and the replenish job's entries (REPLENISH/CLOSE)
const ACTION = {
  CREATE: { label: "Set by HR", tone: "blue" },
  UPDATE: { label: "Adjusted", tone: "amber" },
  REPLENISH: { label: "Replenished", tone: "green" },
  CLOSE: { label: "Cycle closed", tone: "gray" },
};

/** Allocation changes for one employee, newest first; year (of the cycle start) filter or all years. */
const AllocationHistoryDrawer = ({
  employee,
  year: pageYear,
  yearOptions,
  onClose,
}) => {
  const [year, setYear] = useState(String(pageYear));
  const query = useAllocationHistory(
    employee.id,
    year === "all" ? null : Number(year),
  );
  const items = query.data || [];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="app-drawer-backdrop" onClick={onClose}>
      <aside
        className="app-drawer d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alloc-history-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-dialog-header">
          <div>
            <h5 id="alloc-history-title" className="fw-bold text-navy m-0">
              Allocation history — {allocationName(employee)}
            </h5>
            <div className="text-slate-500 fs-080 mt-1">
              {employee.employeeId}
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          ></button>
        </div>

        <div className="app-drawer-body">
          <div className="d-flex align-items-center gap-2 mb-3">
            <label htmlFor="alloc-history-year" className="profile-label m-0">
              Year
            </label>
            <select
              id="alloc-history-year"
              className="form-select form-select-sm w-auto"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  Cycles starting {y}
                </option>
              ))}
              <option value="all">All years</option>
            </select>
            {query.isFetching && (
              <span
                className="spinner-border spinner-border-sm text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            )}
          </div>

          {query.isError && (
            <div className="alert alert-danger fs-085">
              {getApiErrorMessage(query.error, "Could not load the history.")}
            </div>
          )}
          {!query.isLoading && !query.isError && items.length === 0 && (
            <p className="fs-085 text-slate-500">
              No allocation changes{year === "all" ? "" : ` in ${year}`}.
              Changes made before this feature was released aren't recorded.
            </p>
          )}

          <ol className="alloc-history">
            {items.map((h) => {
              const action = ACTION[h.action] || {
                label: h.action,
                tone: "gray",
              };
              return (
                <li key={h.id}>
                  <div className="d-flex justify-content-between gap-2">
                    <strong className="fs-085">
                      {h.policy?.name || h.leaveType?.name || "Leave"}
                      <span
                        className={`status-pill status-pill--${action.tone} ms-2`}
                      >
                        {action.label}
                      </span>
                    </strong>
                    <span className="text-slate-500 fs-075 text-nowrap">
                      {formatDateTime(h.createdAt)}
                    </span>
                  </div>
                  {h.periodStart && (
                    <div className="text-slate-500 fs-075">
                      Cycle {formatPlainDate(h.periodStart)} –{" "}
                      {formatPlainDate(h.periodEnd)}
                    </div>
                  )}
                  {h.action === "CLOSE" ? (
                    <div className="fs-085">
                      Ended with <strong>{h.lapsedDays ?? 0}</strong> unused
                      day(s) lapsed
                      {h.carriedOver ? `, ${h.carriedOver} carried over` : ""}
                    </div>
                  ) : (
                    <div className="fs-085">
                      {h.previousAllocated ?? "none"}{" "}
                      <i className="bi bi-arrow-right text-slate-400"></i>{" "}
                      <strong>{h.allocated}</strong> days
                      {h.carriedOver ? (
                        <span className="text-slate-500">
                          {" "}
                          (incl. {h.carriedOver} carried over)
                        </span>
                      ) : null}
                    </div>
                  )}
                  <div className="text-slate-500 fs-075">
                    by {h.changedBy?.name || "Unknown user"}
                  </div>
                  {h.reason && (
                    <div className="fst-italic fs-080 text-slate-600 mt-1">
                      {h.reason}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
    </div>
  );
};

export default AllocationHistoryDrawer;
